<template>  <div id="pageMovimiento" @click="handleClickOutside">
    <!-- Header flotante para tabla -->
    <div v-if="viewMode === 'table' && showFloatingHeader" class="floating-header">
      <v-card class="elevation-8">
        <v-card-text class="pa-2">
          <v-row no-gutters align="center">
            <v-col v-for="header in headers" :key="header.value" class="text-caption font-weight-bold text-center">
              {{ header.text }}
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <v-container fluid>
      <!-- Filtro de rango de fechas estilo chip -->
      <v-row class="mb-2" no-gutters>
        <v-col cols="auto">
          <v-menu
            v-model="datePickerMenu"
            :close-on-content-click="false"
            offset-y
            max-width="290"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                outlined
                :color="dateRange.length === 2 ? 'primary' : 'grey'"
                class="mr-2"
                close-icon="mdi-close"
                :close="dateRange.length === 2"
                @click:close="clearDateRange"
              >
                <v-icon left small>mdi-calendar-range</v-icon>
                <span v-if="dateRange.length === 2">{{ formattedDateRange }}</span>
                <span v-else>Filtrar por fecha</span>
              </v-chip>
            </template>
            <v-date-picker 
              v-model="dateRange" 
              range
              @change="onDateRangeChange"
            >
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <!-- Estadísticas básicas -->
      <v-row class="mb-3" no-gutters justify="center">
        <!-- Totales -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="primary" size="28" class="mb-1">mdi-car-arrow-right</v-icon>
              <div class="text-h6 font-weight-bold">{{ totalMovimientos }}</div>
              <div class="text-caption grey--text">Totales</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- No reportados -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="red" size="28" class="mb-1">mdi-alert-circle-outline</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementUnreported }}</div>
              <div class="text-caption grey--text">No reportados</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Reportados -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="orange" size="28" class="mb-1">mdi-bell-ring-outline</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementReported }}</div>
              <div class="text-caption grey--text">Reportados</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- En proceso -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="purple" size="28" class="mb-1">mdi-progress-clock</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementProcessing }}</div>
              <div class="text-caption grey--text">En proceso</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Completados -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="green" size="28" class="mb-1">mdi-check-circle-outline</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementCompleted }}</div>
              <div class="text-caption grey--text">Completados</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Vista principal -->
      <v-row>
        <v-col cols="12">
          <v-card class="movement-card elevation-2">
            <!-- Header mejorado -->
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-car-arrow-right</v-icon>
              <v-toolbar-title v-if="!$vuetify.breakpoint.mdAndDown">
                Lista de movimientos
              </v-toolbar-title>
              <v-spacer></v-spacer>

              <!-- Búsqueda para escritorio -->
              <v-col cols="3" class="mr-3" v-if="!$vuetify.breakpoint.mdAndDown">
                <v-text-field v-model="filter.search" label="Buscar movimiento..." prepend-inner-icon="mdi-magnify"
                  hide-details clearable rounded single-line filled dense dark @input="applyFilter"></v-text-field>
              </v-col>

              <!-- Búsqueda para móvil -->
              <v-tooltip top v-if="$vuetify.breakpoint.mdAndDown && !showSearch">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="toggleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <span>Buscar</span>
              </v-tooltip>

              <v-col cols="12" v-if="showSearch && $vuetify.breakpoint.mdAndDown">
                <v-text-field ref="searchInput" v-model="filter.search" label="Buscar movimiento..."
                  prepend-inner-icon="mdi-magnify" hide-details clearable rounded single-line filled dense dark
                  class="search-input-mobile" @click.stop @input="applyFilter"
                  @keydown.esc="showSearch = false"></v-text-field>
              </v-col>

              <!-- Menú de filtros -->
              <filter-menu title="Filtrar movimientos" :current-filter="currentFilter" @canceled="rollbackFilter()"
                @filtered="commitFilter()">
                <v-row>
                  <v-col>
                    <v-text-field v-model="filter['icc_number']" label="Número IIC" clearable outlined
                      hide-details="auto" class="mb-4"
                      prepend-inner-icon="mdi-card-account-details-outline"></v-text-field>
                      <v-text-field v-model="filter['plate']" label="Matrícula" clearable outlined
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-card-bulleted-outline"></v-text-field>

                    <v-text-field v-model="filter['frame_number']" label="Bastidor" clearable outlined
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-identifier"></v-text-field>

                    <v-select v-model="filter['status']" :items="estados" item-text="name" item-value="value"
                      label="Estado" clearable outlined hide-details="auto"
                      prepend-inner-icon="mdi-flag-variant-outline" class="mb-4">
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="getStatusColor(item.value)">
                          {{ getStatusIcon(item.value) }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="getStatusColor(item.value)">
                          {{ getStatusIcon(item.value) }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                    </v-select>

                    <!-- Filtro de ubicación de origen -->
                    <v-select
                      v-model="filter['source_location_id']"
                      :items="locationItems"
                      item-value="id"
                      item-text="displayName "
                      label="Ubicación de Origen"
                      clearable
                      outlined
                      hide-details="auto"
                      prepend-inner-icon="mdi-map-marker"
                      class="mb-4"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small color="primary">mdi-map-marker</v-icon>
                        {{ item.displayName }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left color="primary">mdi-map-marker</v-icon>
                        {{ item.displayName }}
                      </template>
                    </v-select>

                    <!-- Filtro de ubicación de destino -->
                    <v-select
                      v-model="filter['destination_location_id']"
                      :items="locationItems"
                      item-value="id"
                      item-text="displayName "
                      label="Ubicación de Destino"
                      clearable
                      outlined
                      hide-details="auto"
                      prepend-inner-icon="mdi-map-marker-check"
                      class="mb-4"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small color="success">mdi-map-marker-check</v-icon>
                        {{ item.displayName  }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left color="success">mdi-map-marker-check</v-icon>
                        {{ item.displayName  }}
                      </template>
                    </v-select>

                    <!-- Campo de filtro por dealer (solo para colaboradores) -->
                    <v-select 
                      v-if="$store.getters.HAS_ROLE(['colaborator']) || hasDealersAsociados || hasPartnerDealers" 
                      v-model="filter.dealer_id" 
                      :items="accessibleDealers" 
                      :loading="loadingAccessibleDealers"
                      item-value="id" 
                      item-text="name" 
                      label="Dealer" 
                      clearable 
                      outlined 
                      hide-details="auto" 
                      prepend-inner-icon="mdi-store"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="item.is_own ? 'primary' : 'grey'">
                          {{ item.is_own ? 'mdi-home' : 'mdi-store' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar size="24" class="mr-2 ml-0">
                          <img v-if="item.imageUrl && item.imageUrl.url" :src="item.imageUrl.url" alt="Logo dealer">
                          <v-icon v-else :color="item.is_own ? 'primary' : 'grey'">
                            {{ item.is_own ? 'mdi-home' : 'mdi-store' }}
                          </v-icon>
                        </v-list-item-avatar>
                        {{ item.name }}
                        <v-chip v-if="item.is_own" x-small class="ml-2" color="primary">Propio</v-chip>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </filter-menu>

              <!-- Botón agregar movimiento -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <div v-if="HAS_PERMISSION('crear_movimiento')">
                    <v-btn icon v-on="on" @click="showMovimientoAdd()" class="mr-2" color="white">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </template>
                <span>Agregar Movimiento</span>
              </v-tooltip>

              <!-- Botón actualizar -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="refreshDataTable" v-on="on" class="mr-2" color="white" :disabled="loadingItems">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualizar lista</span>
              </v-tooltip>
            </v-toolbar>

            <!-- Formulario modal -->
            <movimiento-form v-if="visibleForm" v-model="editItem" @save="updateDataTable"
              @cancel="visibleForm = false"></movimiento-form>

            <!-- Filtros rápidos bajo la barra de herramientas -->
            <v-card-text class="py-2 px-4">
              <!-- Fila 1: Filtros por condición y ubicación -->
              <v-row class="mb-2" v-if="hasVehicleConditionPermissions">
                <v-col cols="auto">
                  <div class="d-flex flex-wrap align-center py-0">
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vn') && HAS_PERMISSION('vn')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('new')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('new') ? 'green' : 'green'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('new')"
                    >
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      VN
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vo') && HAS_PERMISSION('vo')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('used')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('used') ? 'amber' : 'amber'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('used')"
                    >
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      VO
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('taller') && HAS_PERMISSION('taller')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('taller')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('taller') ? 'indigo' : 'indigo'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('taller')"
                    >
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>

                    <!-- Filtro por checkbox de validación -->
                    <v-chip
                      v-if="HAS_PERMISSION('view_chekbox_validacion')"
                      :outlined="filter.validation_checkbox !== 1"
                      color="green"
                      small
                      class="mr-1"
                      @click="filter.validation_checkbox = filter.validation_checkbox === 1 ? null : 1; commitFilter()"
                    >
                      <v-icon x-small>mdi-check-circle</v-icon>
                    </v-chip>
                    <v-chip
                      v-if="HAS_PERMISSION('view_chekbox_validacion')"
                      :outlined="filter.validation_checkbox !== 0"
                      color="red"
                      small
                      class="mr-1"
                      @click="filter.validation_checkbox = filter.validation_checkbox === 0 ? null : 0; commitFilter()"
                    >
                      <v-icon x-small>mdi-checkbox-blank-circle-outline</v-icon>
                    </v-chip>

                    <!-- Filtro de ubicación de origen -->
                    <v-menu offset-y :close-on-content-click="false" max-width="280" class="ml-15">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          small
                          :color="filter['source_location_id'] && filter['source_location_id'].length > 0 ? 'primary' : 'primary'"
                          :outlined="!(filter['source_location_id'] && filter['source_location_id'].length > 0)"
                          class="mr-1 ml-15 location-filter-chip"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon left x-small :color="filter['source_location_id'] && filter['source_location_id'].length > 0 ? 'white' : 'primary'">
                            mdi-map-marker
                          </v-icon>
                          <span v-if="filter['source_location_id'] && filter['source_location_id'].length > 0">
                            Origen ({{ filter['source_location_id'].length }})
                          </span>
                          <span v-else>Origen</span>
                          <v-icon 
                            v-if="filter['source_location_id'] && filter['source_location_id'].length > 0" 
                            right 
                            x-small 
                            class="ml-1"
                            @click.stop="clearSourceLocation"
                          >
                            mdi-close-circle
                          </v-icon>
                        </v-chip>
                      </template>
                      <v-card>
                        <v-card-title class="py-2 px-3 subtitle-2">
                          <v-icon small color="black" class="mr-2">mdi-map-marker</v-icon>
                          Ubicación de Origen
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-list dense max-height="300" class="overflow-y-auto">
                          <!-- Opción "Sin ubicación" -->
                          <v-list-item
                            @click="toggleSourceLocation(-1)"
                            :class="{ 'primary--text': filter['source_location_id'] && filter['source_location_id'].includes(-1) }"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon small :color="filter['source_location_id'] && filter['source_location_id'].includes(-1) ? 'primary' : 'grey'">
                                {{ filter['source_location_id'] && filter['source_location_id'].includes(-1) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Sin ubicación</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider></v-divider>
                          <!-- Resto de ubicaciones -->
                          <v-list-item
                            v-for="location in locationItems"
                            :key="location.id"
                            @click="toggleSourceLocation(location.id)"
                            :class="{ 'primary--text': filter['source_location_id'] && filter['source_location_id'].includes(location.id) }"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon small :color="filter['source_location_id'] && filter['source_location_id'].includes(location.id) ? 'primary' : 'grey'">
                                {{ filter['source_location_id'] && filter['source_location_id'].includes(location.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ location.displayName  }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card>
                    </v-menu>

                    <!-- Filtro de ubicación de destino -->
                    <v-menu offset-y :close-on-content-click="false" max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          small
                          :color="filter['destination_location_id'] && filter['destination_location_id'].length > 0 ? 'success' : 'success'"
                          :outlined="!(filter['destination_location_id'] && filter['destination_location_id'].length > 0)"
                          class="mr-1 location-filter-chip"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon left x-small :color="filter['destination_location_id'] && filter['destination_location_id'].length > 0 ? 'white' : 'success'">
                            mdi-map-marker-check
                          </v-icon>
                          <span v-if="filter['destination_location_id'] && filter['destination_location_id'].length > 0">
                            Destino ({{ filter['destination_location_id'].length }})
                          </span>
                          <span v-else>Destino</span>
                          <v-icon 
                            v-if="filter['destination_location_id'] && filter['destination_location_id'].length > 0" 
                            right 
                            x-small 
                            class="ml-1"
                            @click.stop="clearDestinationLocation"
                          >
                            mdi-close-circle
                          </v-icon>
                        </v-chip>
                      </template>
                      <v-card>
                        <v-card-title class="py-2 px-3 subtitle-2">
                          <v-icon small color="success" class="mr-2">mdi-map-marker-check</v-icon>
                          Ubicación de Destino
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-list dense max-height="300" class="overflow-y-auto">
                          <v-list-item
                            v-for="location in locationItems"
                            :key="location.id"
                            @click="toggleDestinationLocation(location.id)"
                            :class="{ 'success--text': filter['destination_location_id'] && filter['destination_location_id'].includes(location.id) }"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon small :color="filter['destination_location_id'] && filter['destination_location_id'].includes(location.id) ? 'success' : 'grey'">
                                {{ filter['destination_location_id'] && filter['destination_location_id'].includes(location.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ location.displayName  }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card>
                    </v-menu>
                  </div>
                </v-col>
              </v-row>

              <!-- Fila 2: Filtros por estado y controles -->
              <v-row class="align-center justify-center">
                <v-col cols="auto">
                  <v-chip-group v-model="quickFilter" @change="applyQuickFilter">
                    <v-chip filter outlined color="red" small class="mr-1">
                      <v-icon left x-small>mdi-alert-circle-outline</v-icon>
                      No reportados
                    </v-chip>
                    <v-chip filter outlined color="orange" small class="mr-1">
                      <v-icon left x-small>mdi-bell-ring-outline</v-icon>
                      Reportados
                    </v-chip>
                    <v-chip filter outlined color="purple" small class="mr-1">
                      <v-icon left x-small>mdi-progress-clock</v-icon>
                      En proceso
                    </v-chip>
                    <v-chip filter outlined color="green" small class="mr-1">
                      <v-icon left x-small>mdi-check-circle-outline</v-icon>
                      Completados
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col cols="auto" v-if="hasActiveFilters">
                  <v-btn x-small text color="primary" @click="resetFilters" class="ml-2">
                    <v-icon left small>mdi-filter-remove</v-icon>
                    Limpiar filtros
                  </v-btn>
                </v-col>

                <v-col cols="auto" class="ml-auto">
                  <v-btn-toggle v-model="viewMode" mandatory dense color="primary">
                    <v-btn value="cards" small>
                      <v-icon small>mdi-view-grid-outline</v-icon>
                      Tarjetas
                    </v-btn>
                    <v-btn value="table" small>
                      <v-icon small>mdi-table</v-icon>
                      Tabla
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Filtros aplicados -->
              <v-row v-if="hasActiveFilters" class="mt-1 justify-center">
                <v-col cols="12">
                  <div class="d-flex align-center flex-wrap justify-center">
                    <span class="text-caption mr-2 grey--text">Filtros aplicados:</span>

                    <!-- Filtro de Condición -->
                    <v-chip v-if="filter.vehicle_condition === 'new'" x-small class="mr-1 mb-1" close
                      color="green" text-color="white" @click:close="filter.vehicle_condition = null; conditionFilter = null; commitFilter()">
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      VN
                    </v-chip>

                    <v-chip v-if="filter.vehicle_condition === 'used'" x-small class="mr-1 mb-1" close
                      color="amber" text-color="white" @click:close="filter.vehicle_condition = null; conditionFilter = null; commitFilter()">
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      VO
                    </v-chip>

                    <v-chip v-if="filter.vehicle_condition === 'taller'" x-small class="mr-1 mb-1" close
                      color="indigo" text-color="white" @click:close="filter.vehicle_condition = null; conditionFilter = null; commitFilter()">
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>

                    <v-chip v-if="filter['icc_number']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['icc_number'] = null; commitFilter()">
                      IIC: {{ filter['icc_number'] }}
                    </v-chip>
                    <v-chip v-if="filter['vehicle.brand']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['vehicle.brand'] = null; commitFilter()">
                      Marca: {{ filter['vehicle.brand'] }}
                    </v-chip>
                    <v-chip v-if="filter['vehicle.model']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['vehicle.model'] = null; commitFilter()">
                      Modelo: {{ filter['vehicle.model'] }}
                    </v-chip>
                    <v-chip v-if="filter['plate']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['plate'] = null; commitFilter()">
                      Matrícula: {{ filter['plate_number'] }}
                    </v-chip>
                    <v-chip v-if="filter['frame_number']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['frame_number'] = null; commitFilter()">
                      Bastidor: {{ filter['frame_number'] }}
                    </v-chip>
                    <v-chip v-if="filter['source_location_id'] && filter['source_location_id'].length > 0" x-small class="mr-1 mb-1" close color="primary"
                      text-color="white" @click:close="filter['source_location_id'] = []; commitFilter()">
                      Origen ({{ filter['source_location_id'].length }})
                    </v-chip>
                    <v-chip v-if="filter['destination_location_id'] && filter['destination_location_id'].length > 0" x-small class="mr-1 mb-1" close color="success"
                      text-color="white" @click:close="filter['destination_location_id'] = []; commitFilter()">
                      Destino ({{ filter['destination_location_id'].length }})
                    </v-chip>
                    <v-chip v-if="filter['dealer_id']" x-small class="mr-1 mb-1" close color="purple"
                      text-color="white" @click:close="filter['dealer_id'] = null; commitFilter()">
                      Dealer: {{ getDealerName(filter['dealer_id']) }}
                    </v-chip>
                    <v-chip v-if="filter['status']" x-small class="mr-1 mb-1" close
                      :color="getStatusColor(filter['status'])" text-color="white"
                      @click:close="filter['status'] = null; commitFilter()">
                      {{ translateStatus(filter['status']) }}
                    </v-chip>
                    <v-chip v-if="filter.validation_checkbox !== null" x-small class="mr-1 mb-1" close color="teal"
                      text-color="white" @click:close="filter.validation_checkbox = null; commitFilter()">
                      <v-icon left x-small>mdi-checkbox-marked-circle-outline</v-icon>
                      {{ filter.validation_checkbox === 1 ? 'Validados' : 'Sin validar' }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Estado de carga -->
            <div v-if="loadingItems" class="pa-3">
              <!-- Skeleton loaders para la vista de tabla -->
              <template v-if="viewMode === 'table'">
                <v-skeleton-loader type="table-heading" class="mb-2"></v-skeleton-loader>
                <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
              </template>

              <!-- Skeleton loaders para vista de tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 8" :key="`skeleton-${n}`">
                  <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </div>            <!-- Vista de tabla -->
            <div v-else-if="viewMode === 'table'" class="sticky-table-container">
              <v-data-table :headers="headers" :items="MOVIMIENTOS" :options.sync="pagination"
                :server-items-length="totalItems" :loading="loadingItems" :footer-props="footer" :key="tableId"
                class="elevation-0 sticky-table"
                :multi-sort="false"
                fixed-header height="calc(100vh - 300px)">
                <!-- Columna de vehículo -->
                <template v-slot:item.vehicle="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar color="blue-grey lighten-4" size="36" class="mr-3">
                      <v-icon size="18" color="blue-grey darken-1">mdi-car</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.vehicle.brand }} {{ item.vehicle.model }}</div>
                      <div class="caption grey--text text--darken-1">
                        {{ item.vehicle.plate_number || 'Sin matrícula' }} | IIC: {{ item.vehicle.iic_number || 'N/A' }} | Bastidor: {{ (item.vehicle.frame_number && item.vehicle.frame_number.length > 6) ? '...' + item.vehicle.frame_number.slice(-6) : item.vehicle.frame_number }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Columna de ubicaciones -->
                <template v-slot:item.locations="{ item }">
                  <div class="d-flex align-center pa-2 rounded" 
                       :class="{ 'orange lighten-5': item.additional_info && item.additional_info.trim() !== '' && item.status !== 'completed' }">
                    <v-icon small color="blue-grey" class="mr-1">mdi-map-marker</v-icon>
                    <div class="grey--text text--darken-2">{{ item.type !== 'entry' ? item.sourceLocation?.name || 'N/A' : 'Sin ubicación' }}</div>
                    <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                    <div>{{ item.destinationLocation?.name || 'N/A' }}</div>
                    <v-btn 
                      v-if="item.additional_info && item.additional_info.trim() !== '' && item.status !== 'completed'" 
                      icon 
                      x-small 
                      color="orange darken-2" 
                      class="ml-2"
                      @click="showAdditionalInfoDialog(item)"
                    >
                      <v-icon small>mdi-alert-circle-outline</v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- Dealer (solo para colaboradores) -->
                <template v-slot:item.dealer="{ item }">
                  <div v-if="item.vehicle && item.vehicle.dealer" class="d-flex align-center">
                    <v-avatar size="24" class="mr-2">
                      <img v-if="item.vehicle.dealer.imageUrl && item.vehicle.dealer.imageUrl.url" 
                           :src="item.vehicle.dealer.imageUrl.url" 
                           alt="Logo dealer">
                      <v-icon v-else size="16" :color="item.vehicle.dealer.is_colaborador ? 'primary' : 'grey'">
                        {{ item.vehicle.dealer.is_colaborador ? 'mdi-home' : 'mdi-store' }}
                      </v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.vehicle.dealer?.name || 'N/A' }}</div>
                      <div class="caption grey--text text--darken-1">{{ item.vehicle.dealer?.city || '' }}</div>
                    </div>
                  </div>
                  <div v-else class="grey--text">N/A</div>
                </template>

                <!-- Columna de fecha -->
                <template v-slot:item.datetime="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-calendar</v-icon>
                    {{ formatDate(getMovementDate(item)) }}
                  </div>
                </template>

                <!-- Columna de fecha de creación -->
                <template v-slot:item.created_at="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-clock-outline</v-icon>
                    {{ formatDateHours(item.created_at) }}
                  </div>
                </template>

                <!-- Columna de fecha de fin -->
                <template v-slot:item.updated_at="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-update</v-icon>
                    {{ formatDateHours(item.updated_at) }}
                  </div>
                </template>

                <!-- Columna de responsable -->
                <template v-slot:item.responsible="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-account-tie</v-icon>
                    {{ item.responsible ? (item.responsible.name + ' ' + item.responsible.lastname) : 'No asignado' }}
                  </div>
                </template>

                <!-- Columna de checkbox de validación -->
                <template v-slot:item.validation_checkbox="{ item }">
                  <div v-if="(item.status === 'unreported' || item.status === 'reported') && HAS_PERMISSION('view_chekbox_validacion')" class="d-flex justify-center align-center">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          small
                          v-on="on"
                          :color="item.validation_checkbox ? 'success' : 'red'"
                          :disabled="!HAS_PERMISSION('usage_chekbox_validacion') || loadingCheckboxIds.includes(item.id)"
                          :loading="loadingCheckboxIds.includes(item.id)"
                          @click="toggleValidationCheckbox(item)"
                        >
                          <v-icon>{{ item.validation_checkbox ? 'mdi-check-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ item.validation_checkbox ? 'Validado' : 'Sin validar' }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Columna de estado -->
                <template v-slot:item.status="{ item }">                  <v-chip :color="getStatusColor(item.status)" small dark>
                    <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
                    {{ translateStatus(item.status) }}
                  </v-chip>
                </template>

                <!-- Columna de acciones -->
                <template v-slot:item.action="{ item }">
                  <div class="d-inline-flex action-buttons">
                    <!-- Botón Editar -->
                    <v-tooltip top v-if="item.status !== 'completed' && canEditMovimiento(item)">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="primary" @click="showMovimientoForm(item)" v-on="on">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>

                    <!-- Botón Ver Detalles -->
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="info"
                          :to="{ name: 'MovimientoDetail', params: { movimiento_id: item.id } }" v-on="on">
                          <v-icon>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver detalles</span>
                    </v-tooltip>

                    <!-- Botón Aceptar Movimiento -->
                    <v-tooltip top v-if="canAcceptMovimiento(item)">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="success" @click="acceptMovimiento(item)" v-on="on">
                          <v-icon>mdi-check-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>Aceptar movimiento</span>
                    </v-tooltip>

                    <!-- Botón Eliminar Movimiento -->
                    <v-tooltip top v-if="canDeleteMovimiento(item)" >
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="red" @click="deleteMovimiento(item)" v-on="on">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Eliminar movimiento</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Sin resultados -->
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron movimientos</h3>
                    <p class="subtitle-1 grey--text mb-4">
                      Intenta con otros filtros o agrega un nuevo movimiento
                    </p>
                    <v-btn color="primary" outlined @click="showMovimientoAdd()">
                      <v-icon left>mdi-plus</v-icon>
                      Agregar movimiento
                    </v-btn>
                  </div>                </template>
              </v-data-table>
            </div>

            <!-- Vista de tarjetas -->
            <v-card-text v-else class="pa-3">
              <v-row>
                <v-col cols="12" sm="6" md="4" v-for="item in MOVIMIENTOS" :key="item.id">
                  <v-card outlined hover class="movement-item-card h-100">
                    <v-card-text class="pa-4">
                      <!-- Header de tarjeta con estado -->
                      <div class="d-flex justify-space-between align-center mb-3">
                        <v-chip :color="getStatusColor(item.status)" small dark>
                          <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
                          {{ translateStatus(item.status) }}
                        </v-chip>
                        <v-menu bottom left offset-y transition="slide-y-transition">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon x-small v-bind="attrs" v-on="on">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item @click="showMovimientoForm(item)" v-if="item.status !== 'completed' && canEditMovimiento(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small>mdi-pencil</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Editar</v-list-item-title>
                            </v-list-item>
                            <v-list-item :to="{ name: 'MovimientoDetail', params: { movimiento_id: item.id } }">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="info">mdi-eye</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Ver detalles</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="acceptMovimiento(item)" v-if="canAcceptMovimiento(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="success">mdi-check-circle</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title class="success--text">Aceptar movimiento</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="deleteMovimiento(item)" v-if="canDeleteMovimiento(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="red">mdi-delete</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title class="red--text">Eliminar movimiento</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>

                      <!-- Vehículo -->
                      <div class="d-flex mb-4">
                        <v-avatar size="56" color="blue-grey lighten-4" class="mr-3">
                          <v-icon size="32" color="blue-grey darken-1">mdi-car</v-icon>
                        </v-avatar>
                        <div>
                          <div class="subtitle-1 font-weight-medium mb-1">
                            {{ item.vehicle.brand }} {{ item.vehicle.model }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            <v-icon x-small class="mr-1">mdi-card-bulleted-outline</v-icon>
                            {{ item.vehicle.plate_number || 'Sin matrícula' }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            <v-icon x-small class="mr-1">mdi-card-account-details-outline</v-icon>
                            IIC: {{ item.vehicle.iic_number || 'N/A' }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                            Bastidor: {{ (item.vehicle.frame_number && item.vehicle.frame_number.length > 6) ? '...' + item.vehicle.frame_number.slice(-6) : item.vehicle.frame_number }}
                          </div>
                        </div>
                      </div>

                      <!-- Información básica -->
                      <v-list dense class="pa-0 transparent">
                        <!-- Origen y destino -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-map-marker-path</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle class="d-flex align-center pa-2 rounded"
                                                   :class="{ 'orange lighten-5': item.additional_info && item.additional_info.trim() !== '' && item.status !== 'completed' }">
                              <span class="grey--text text--darken-2">{{ item.sourceLocation?.name || 'N/A' }}</span>
                              <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                              <span>{{ item.destinationLocation?.name || 'N/A' }}</span>
                              <v-btn 
                                v-if="item.additional_info && item.additional_info.trim() !== '' && item.status !== 'completed'" 
                                icon 
                                x-small 
                                color="orange darken-2" 
                                class="ml-2"
                                @click="showAdditionalInfoDialog(item)"
                              >
                                <v-icon small>mdi-alert-circle-outline</v-icon>
                              </v-btn>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha de movimiento -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-calendar</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ formatDate(getMovementDate(item)) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha de creación -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-clock-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ formatDateHours(item.created_at) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha de actualización -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-update</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ formatDateHours(item.updated_at) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Responsable -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-account-tie</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.responsible ? (item.responsible.name + ' ' + item.responsible.lastname) : 'No asignado' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Checkbox de validación -->
                        <v-list-item class="px-0 py-1" v-if="(item.status === 'unreported' || item.status === 'reported') && HAS_PERMISSION('view_chekbox_validacion')">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-checkbox-marked-circle-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle class="d-flex align-center">
                              <span class="mr-2">Validación</span>
                              <v-btn
                                icon
                                x-small
                                :color="item.validation_checkbox ? 'success' : 'red'"
                                :disabled="!HAS_PERMISSION('usage_chekbox_validacion') || loadingCheckboxIds.includes(item.id)"
                                :loading="loadingCheckboxIds.includes(item.id)"
                                @click="toggleValidationCheckbox(item)"
                              >
                                <v-icon small>{{ item.validation_checkbox ? 'mdi-check-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
                              </v-btn>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Dealer info (solo para colaboradores) -->
                        <v-list-item v-if="($store.getters.HAS_ROLE(['colaborator']) || hasDealersAsociados || hasPartnerDealers) && item.vehicle && item.vehicle.dealer" class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small :color="item.vehicle.dealer.is_colaborador ? 'primary' : 'blue-grey darken-1'">
                              {{ item.vehicle.dealer.is_colaborador ? 'mdi-home' : 'mdi-store' }}
                            </v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle class="d-flex align-center">
                              <v-avatar v-if="item.vehicle.dealer.imageUrl && item.vehicle.dealer.imageUrl.url" size="16" class="mr-1">
                                <img :src="item.vehicle.dealer.imageUrl.url" alt="Logo dealer">
                              </v-avatar>
                              {{ item.vehicle.dealer?.name || 'N/A' }}
                              <v-chip v-if="item.vehicle.dealer.is_colaborador" x-small class="ml-2" color="primary">
                                Propio
                              </v-chip>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Transporte -->
                        <v-list-item class="px-0 py-1" v-if="item.transportation">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-truck-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.transportation }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>

                      <!-- Acciones -->
                      <v-card-actions class="pa-0 mt-3">
                        <v-btn text small color="primary"
                          :to="{ name: 'MovimientoDetail', params: { movimiento_id: item.id } }">
                          <v-icon left small>mdi-eye</v-icon>
                          Ver detalles
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn v-if="canAcceptMovimiento(item)" text small color="success"
                          @click="acceptMovimiento(item)">
                          <v-icon left small>mdi-check-circle</v-icon>
                          Aceptar
                        </v-btn>
                      </v-card-actions>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Sin resultados -->
              <v-row v-if="!MOVIMIENTOS.length && !loadingItems">
                <v-col cols="12" class="text-center pa-6">
                  <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron movimientos</h3>
                  <p class="subtitle-1 grey--text mb-4">
                    Intenta con otros filtros o agrega un nuevo movimiento
                  </p>
                  <v-btn color="primary" outlined @click="showMovimientoAdd()">
                    <v-icon left>mdi-plus</v-icon>
                    Agregar movimiento
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Paginación -->
              <v-row v-if="MOVIMIENTOS.length > 0">
                <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                  <div class="text-center d-flex align-center mr-4">
                    <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                    <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                      hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                  </div>

                  <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                    :total-visible="7" circle @input="onPageChange"></v-pagination>

                  <span class="text-body-2 grey--text text--darken-1 ml-4">
                    {{ paginationText }}
                  </span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Diálogo: selección tipo de movimiento -->
    <v-dialog v-model="movimientoTypeDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="primary white--text py-3">
          <v-icon left color="white">mdi-car-arrow-right</v-icon>
          Nuevo Movimiento
          <v-spacer></v-spacer>
          <v-btn icon dark @click="movimientoTypeDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-5 pb-4">
          <p class="subtitle-1 grey--text text--darken-1 mb-4 text-center">
            ¿Qué tipo de movimiento deseas crear?
          </p>
          <v-row justify="center">
            <v-col cols="12" sm="5">
              <v-card
                outlined
                hover
                class="type-option-card text-center pa-4"
                @click="selectMovimientoType('individual')"
              >
                <v-icon size="48" color="primary" class="mb-2">mdi-car</v-icon>
                <div class="subtitle-1 font-weight-bold">Individual</div>
                <div class="caption grey--text mt-1">Un solo vehículo</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="5">
              <v-card
                outlined
                hover
                class="type-option-card text-center pa-4"
                @click="selectMovimientoType('multiple')"
              >
                <v-icon size="48" color="purple" class="mb-2">mdi-car-multiple</v-icon>
                <div class="subtitle-1 font-weight-bold">Múltiple</div>
                <div class="caption grey--text mt-1">Varios vehículos a la vez</div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Formulario de movimiento múltiple -->
    <multiple-movimiento-form
      v-model="multipleMovimientoDialog"
      :current-dealer-id="CURRENT_USER && CURRENT_USER.dealer && CURRENT_USER.dealer.id"
      :current-user="CURRENT_USER"
      @save="loadData"
    ></multiple-movimiento-form>

    <!-- Diálogo de información adicional -->
    <v-dialog v-model="additionalInfoDialog" max-width="600">
      <v-card>
        <v-card-title class="headline orange lighten-4">
          <v-icon left color="orange darken-2">mdi-alert-circle-outline</v-icon>
          Información Adicional
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <div v-if="selectedMovement">
            <!-- Información del vehículo -->
            <div class="mb-3">
              <div class="subtitle-2 grey--text text--darken-1 mb-1">Vehículo:</div>
              <div class="body-1 font-weight-medium">
                {{ selectedMovement.vehicle.brand }} {{ selectedMovement.vehicle.model }}
              </div>
              <div class="caption grey--text">
                {{ selectedMovement.vehicle.plate_number || 'Sin matrícula' }} | 
                IIC: {{ selectedMovement.vehicle.iic_number || 'N/A' }}
              </div>
            </div>

            <!-- Ubicaciones -->
            <div class="mb-3">
              <div class="subtitle-2 grey--text text--darken-1 mb-1">Ruta:</div>
              <div class="body-1">
                <v-icon small color="blue-grey" class="mr-1">mdi-map-marker</v-icon>
                {{ selectedMovement.sourceLocation?.name || 'N/A' }}
                <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                {{ selectedMovement.destinationLocation?.name || 'N/A' }}
              </div>
            </div>

            <!-- Información adicional -->
            <div>
              <div class="subtitle-2 grey--text text--darken-1 mb-2">Información:</div>
              <v-card outlined class="pa-3 orange lighten-5">
                <div class="body-1" style="white-space: pre-wrap; word-break: break-word;">
                  {{ selectedMovement.additional_info }}
                </div>
              </v-card>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="additionalInfoDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<!-- 
  SECCIÓN <script> COMPLETA de Movimiento.vue con sistema de partners
  (el <template> y <style> no cambian — solo este bloque)
-->
<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import moment from 'moment-timezone'
import FilterMenu from '../../components/FilterMenu'
import MovimientoService from '@/services/MovimientoService'
import DealerService from '@/services/DealerService'
import errorHandler from '../../mixins/errorHandler'
import LocationService from '@/services/LocationService'
import MultipleMovimientoForm from '@/components/MultipleMovimientoForm.vue'

export default {
  components: {
    MovimientoForm: () => import(/* webpackPrefetch: true */ './MovimientoForm'),
    FilterMenu,
    MultipleMovimientoForm
  },

  mixins: [errorHandler],
  data: () => ({
    loadingItems: false,
    totalItems: 0,
    tableId: 0,
    showSearch: false,
    quickFilter: null,
    conditionFilter: null,
    totalMovimientos: 0,
    movementUnreported: 0,
    movementReported: 0,
    movementProcessing: 0,
    movementCompleted: 0,
    showFloatingHeader: false,
    locationItems: [],
    loadingLocations: false,

    dateRange: [],
    datePickerMenu: false,

    additionalInfoDialog: false,
    selectedMovement: null,
    movimientoTypeDialog: false,
    multipleMovimientoDialog: false,

    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      itemsPerPageText: 'Movimientos por página:',
      pageText: '{0}-{1} de {2}'
    },

    filter: {
      search: null,
      'icc_number': null,
      'vehicle.brand': null,
      'vehicle.model': null,
      'plate': null,
      'frame_number': null,
      'source_location_id': [],
      'destination_location_id': [],
      'status': null,
      dealer_id: null,
      vehicle_condition: [],
      validation_checkbox: null
    },
    currentFilter: {
      search: null,
      'icc_number': null,
      'vehicle.brand': null,
      'vehicle.model': null,
      'plate': null,
      'frame_number': null,
      'source_location_id': [],
      'destination_location_id': [],
      'status': null,
      dealer_id: null,
      vehicle_condition: [],
      validation_checkbox: null
    },
    estados: [
      { name: 'No Reportado', value: 'unreported' },
      { name: 'Reportado', value: 'reported' },
      { name: 'En Proceso', value: 'processing' },
      { name: 'Completado', value: 'completed' }
    ],

    visibleForm: false,
    editItem: null,
    roles: [],

    accessibleDealers: [],
    loadingAccessibleDealers: false,
    manualViewMode: null,
    loadingCheckboxIds: []
  }),

  async created () {
    this.isInitializing = true
    const stats = await MovimientoService.getStats()

    const unreported = (stats.data.data.unreported.move || 0)
    const reported = (stats.data.data.reported.move || 0)
    const processing = (stats.data.data.processing.move || 0)
    const completed = (stats.data.data.completed.exit + stats.data.data.completed.entry || 0)

    this.totalMovimientos = unreported + reported + processing + completed
    this.movementUnreported = unreported
    this.movementReported = reported
    this.movementProcessing = processing
    this.movementCompleted = completed

    this.roles = this.CURRENT_USER_ROLES

    await this.loadAccessibleDealers()
    this.loadData()
    await this.loadLocations()
  },

  mounted () {
    window.addEventListener('scroll', this.handleScroll)
  },

  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    async loadLocations () {
  this.loadingLocations = true
  try {
    const hasPartners = this.$store.getters.IS_PARTNER
    if (hasPartners) {
      const response = await LocationService.getPartnerLocations()
      const rawLocations = response.data?.data?.locations || []
      this.locationItems = rawLocations.map(loc => ({
        ...loc,
        displayName: loc.dealer_name
          ? `${loc.name} (${loc.dealer_name})`
          : loc.name
      }))
    } else {
      // Cargar todas las ubicaciones paginando para evitar límite de 20 por defecto
      let allLocations = []
      let page = 1
      const perPage = 50
      let totalCount = null
      do {
        const response = await LocationService.get({ 'per-page': perPage, page, expand: 'dealer' })
        let locations = []
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          locations = response.data.data
        } else if (Array.isArray(response.data)) {
          locations = response.data
        }
        allLocations = allLocations.concat(locations)
        totalCount = response.data?._meta?.totalCount ?? allLocations.length
        if (locations.length < perPage) break
        if (allLocations.length >= totalCount) break
        page++
      } while (true)
      this.locationItems = allLocations.map(loc => ({
        ...loc,
        displayName: loc.dealer && loc.dealer.name
          ? `${loc.name} (${loc.dealer.name})`
          : loc.name
      }))
    }
  } catch (error) {
    this.$toast && this.$toast.error('Error al cargar ubicaciones')
  } finally {
    this.loadingLocations = false
  }
},
    loadData () {
      this.loadingItems = true

      let filterParams = { ...this.filter, types: 'entry,move,exit' }

      if (filterParams['source_location_id'] && Array.isArray(filterParams['source_location_id'])) {
        if (filterParams['source_location_id'].length > 0) {
          filterParams['source_location_id'] = filterParams['source_location_id'].join(',')
        } else {
          delete filterParams['source_location_id']
        }
      }

      if (filterParams['destination_location_id'] && Array.isArray(filterParams['destination_location_id'])) {
        if (filterParams['destination_location_id'].length > 0) {
          filterParams['destination_location_id'] = filterParams['destination_location_id'].join(',')
        } else {
          delete filterParams['destination_location_id']
        }
      }

      if (this.$store.getters.HAS_ROLE(['colaborator']) && this.filter.dealer_id) {
        filterParams.colaborador_dealer_filter = this.filter.dealer_id
        delete filterParams.dealer_id
      }

      if (filterParams.validation_checkbox === null) {
        delete filterParams.validation_checkbox
      }

      if (this.dateRange && this.dateRange.length === 2) {
        const sortedDates = [...this.dateRange].sort()
        const startDate = new Date(sortedDates[0])
        startDate.setHours(0, 0, 0, 0)
        const endDate = new Date(sortedDates[1])
        endDate.setHours(23, 59, 59, 999)
        filterParams.created_at_from = Math.floor(startDate.getTime() / 1000)
        filterParams.created_at_to = Math.floor(endDate.getTime() / 1000)
      }

      this.$store.dispatch('LOAD_MOVIMIENTOS', filterParams).then(result => {
        this.loadingItems = false
        const maxPage = Math.ceil(result / this.pagination.itemsPerPage)
        if (this.pagination.page > maxPage && maxPage > 0) {
          this.pagination.page = maxPage
        }
        this.totalItems = result
      }).catch(error => {
        this.handleApiError(error, 'Error al cargar los movimientos')
        this.loadingItems = false
      })
    },

    debouncedLoadData: _.debounce(function () { this.loadData() }, 500),

    async loadStats (filterParams = null) {
      try {
        const statsParams = filterParams || this.buildFilterParams()
        let formattedParams = {}
        for (let key in statsParams) {
          if (statsParams.hasOwnProperty(key)) {
            formattedParams[`filter[${key}]`] = statsParams[key]
          }
        }
        const stats = await MovimientoService.getStats(formattedParams)
        const unreported = (stats.data.data.unreported?.move || 0)
        const reported = (stats.data.data.reported?.move || 0)
        const processing = (stats.data.data.processing?.move || 0)
        const completed = (stats.data.data.completed?.exit || 0)
        this.totalMovimientos = unreported + reported + processing + completed
        this.movementUnreported = unreported
        this.movementReported = reported
        this.movementProcessing = processing
        this.movementCompleted = completed
        this.$nextTick(() => { this.$forceUpdate() })
      } catch (error) {
        this.handleApiError(error, 'Error al cargar las estadísticas de movimientos')
      }
    },

    buildFilterParams () {
      const params = { ...this.filter }
      if (params.vehicle_condition && params.vehicle_condition.length === 0) delete params.vehicle_condition
      if (params.dealer_id && params.dealer_id.length === 0) delete params.dealer_id
      if (params.status && params.status.length === 0) delete params.status
      if (params.validation_checkbox === null) delete params.validation_checkbox
      if (this.dateRange && this.dateRange.length === 2) {
        const sortedDates = [...this.dateRange].sort()
        const startDate = new Date(sortedDates[0])
        startDate.setHours(0, 0, 0, 0)
        const endDate = new Date(sortedDates[1])
        endDate.setHours(23, 59, 59, 999)
        params.created_at_from = Math.floor(startDate.getTime() / 1000)
        params.created_at_to = Math.floor(endDate.getTime() / 1000)
      }
      if (this.$store.getters.HAS_ROLE(['colaborator']) && !params.dealer_id) {
        if (this.accessibleDealers.length > 0) {
          params.dealer_id = this.accessibleDealers.map(d => d.id)
        }
      }
      return params
    },

    updateDataTable ({ insert }) {
      this.visibleForm = false
      if (insert) { this.totalItems++ }
      this.refreshDataTable()
      this.$toast.success(insert ? 'Movimiento creado con éxito' : 'Movimiento actualizado con éxito')
    },

    refreshDataTable () {
      this.tableId++
      this.loadData()
      this.loadStats(this.buildFilterParams())
    },

    async loadAccessibleDealers () {
      // Cargar para colaboradores Y para dealers con partners
      try {
        this.loadingAccessibleDealers = true
        const response = await DealerService.getMyAccessibleDealers()
        let dealersArray = []
        if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
          dealersArray = response.data.data.data
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          dealersArray = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          dealersArray = response.data
        }
        this.accessibleDealers = dealersArray
      } catch (error) {
        this.handleApiError(error, 'Error al cargar la lista de dealers')
      } finally {
        this.loadingAccessibleDealers = false
      }
    },

    toggleSearch () {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) { this.$refs.searchInput.focus() }
        })
      }
    },

    handleClickOutside (event) {
      const searchInput = this.$refs.searchInput
      if (searchInput && !searchInput.$el.contains(event.target)) {
        this.showSearch = false
      }
    },

    handleScroll () {
      if (this.viewMode !== 'table') { this.showFloatingHeader = false; return }
      const tableElement = document.querySelector('.v-data-table')
      if (tableElement) {
        const rect = tableElement.getBoundingClientRect()
        this.showFloatingHeader = rect.top < -100
      }
    },

    applyFilter () {
      this.debouncedLoadData()
      this.loadStats(this.buildFilterParams())
    },

    commitFilter () {
      for (const key in this.currentFilter) { this.currentFilter[key] = this.filter[key] }
      this.loadData()
      this.loadStats(this.buildFilterParams())
    },

    rollbackFilter () {
      for (const key in this.currentFilter) { this.filter[key] = this.currentFilter[key] }
    },

    resetFilters () {
      for (const key in this.filter) {
        this.filter[key] = key === 'vehicle_condition' ? [] : null
      }
      this.quickFilter = null
      this.conditionFilter = null
      this.dateRange = []
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },

    applyQuickFilter (index) {
      if (index === null) {
        this.filter.status = null
      } else {
        const statusValues = ['unreported', 'reported', 'processing', 'completed']
        this.filter.status = statusValues[index]
      }
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },

    applyConditionFilter (condition) {
      if (!Array.isArray(this.filter.vehicle_condition)) { this.filter.vehicle_condition = [] }
      const index = this.filter.vehicle_condition.indexOf(condition)
      if (index > -1) {
        this.filter.vehicle_condition.splice(index, 1)
      } else {
        this.filter.vehicle_condition.push(condition)
      }
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },

    getLocationName (locationId) {
      const location = this.locationItems.find(l => l.id === locationId)
      return location ? location.displayName : 'Desconocido'
    },

    getDealerName (dealerId) {
      const dealer = this.accessibleDealers.find(d => d.id === dealerId)
      return dealer ? dealer.name : 'Desconocido'
    },

    toggleSourceLocation (locationId) {
      if (!this.filter['source_location_id']) { this.filter['source_location_id'] = [] }
      const index = this.filter['source_location_id'].indexOf(locationId)
      if (index > -1) {
         this.filter['source_location_id'].splice(index, 1)
      } else { this.filter['source_location_id'].push(locationId) }
      this.commitFilter()
    },

    clearSourceLocation () {
      this.filter['source_location_id'] = []
      this.commitFilter()
    },

    toggleDestinationLocation (locationId) {
      if (!this.filter['destination_location_id']) { this.filter['destination_location_id'] = [] }
      const index = this.filter['destination_location_id'].indexOf(locationId)
      if (index > -1) { 
        this.filter['destination_location_id'].splice(index, 1) 
      } else { this.filter['destination_location_id'].push(locationId) }
      this.commitFilter()
    },

    clearDestinationLocation () {
      this.filter['destination_location_id'] = []
      this.commitFilter()
    },

    onDateRangeChange (value) {
      if (value && value.length === 2) {
        this.datePickerMenu = false
        this.loadData()
        this.loadStats(this.buildFilterParams())
      }
    },

    clearDateRange () {
      this.dateRange = []
      this.loadData()
      this.loadStats(this.buildFilterParams())
    },

    showMovimientoForm (item) {
      this.editItem = item ? { ...item } : null
      this.visibleForm = true
    },

    showMovimientoAdd () {
      this.movimientoTypeDialog = true
    },

    selectMovimientoType (type) {
      this.movimientoTypeDialog = false
      if (type === 'individual') {
        this.$router.push({ name: 'Escaneo' })
      } else if (type === 'multiple') {
        this.multipleMovimientoDialog = true
      }
    },

    showAdditionalInfoDialog (item) {
      this.selectedMovement = item
      this.additionalInfoDialog = true
    },

    // -------------------------------------------------------
    // PERMISOS — sistema partner integrado
    // -------------------------------------------------------

    /**
     * El usuario puede aceptar (unreported → reported) si:
     * 1. Si el vehículo es de un dealer partner: SOLO verifica permiso cruzado 'aceptar_movimiento' en ese partner
     * 2. Si el vehículo es de su dealer propio: verifica permiso local 'aceptar_movimiento'
     * 3. La ubicación origen/destino es de su dealer propio
     */
    canAcceptMovimiento (item) {
      if (item.status !== 'unreported') return false
      if (!this.CURRENT_USER?.dealer) return false

      const vehicleDealerId = item.vehicle?.dealer_id
      const userDealerId = this.CURRENT_USER.dealer.id

      // Si es colaborador, siempre verificar permiso local en su dealer
      if (this.$store.getters.HAS_ROLE(['colaborator'])) {
        return this.HAS_PERMISSION('aceptar_movimiento')
      }

      // Si el vehículo es de un dealer partner, SOLO verificar permiso cruzado (no verificar permiso local)
      if (vehicleDealerId && userDealerId && vehicleDealerId !== userDealerId) {
        return this.$store.getters.HAS_PARTNER_PERMISSION({
          dealerId: vehicleDealerId,
          permission: 'aceptar_movimiento'
        })
      }

      // Si el vehículo es del dealer propio, verificar permiso local
      if (this.HAS_PERMISSION('aceptar_movimiento')) return true

      // Ubicación origen/destino del dealer propio
      const srcDealer = item.sourceLocation?.dealer_id
      const dstDealer = item.destinationLocation?.dealer_id
      if (srcDealer === userDealerId || dstDealer === userDealerId) return true

      return false
    },

    /**
     * El usuario puede editar el movimiento si:
     * - Si es de un dealer partner: SOLO verifica permiso cruzado 'editar_movimiento'
     * - Si es de su dealer propio: verifica permiso RBAC 'editar_movimiento'
     * - Es colaborador → pertenece al dealer asociado
     */
    canEditMovimiento (item) {
      if (item.status === 'completed') return false
      if (!item.vehicle?.dealer) return false

      const vehicleDealerId = item.vehicle.dealer.id
      const myDealerId = this.CURRENT_USER?.dealer?.id

      // Colaborador: solo movimientos de su propio dealer (lógica original)
      if (this.$store.getters.HAS_ROLE(['colaborator'])) {
        if (!myDealerId) return false
        return item.status !== 'unreported' && vehicleDealerId === myDealerId
      }

      // Si el vehículo es de un dealer partner, SOLO verificar permiso cruzado (no verificar permiso local)
      if (vehicleDealerId !== myDealerId) {
        return this.$store.getters.HAS_PARTNER_PERMISSION({
          dealerId: vehicleDealerId,
          permission: 'editar_movimiento'
        })
      }

      // Si el vehículo es del dealer propio, verificar permiso local
      return this.HAS_PERMISSION('editar_movimiento')
    },

    /**
     * El usuario puede eliminar el movimiento si:
     * - Si es de un dealer partner: SOLO verifica permiso cruzado 'eliminar_movimiento'
     * - Si es de su dealer propio: verifica permiso RBAC 'eliminar_movimiento'
     * - Es colaborador → solo los de su propio dealer
     */
    canDeleteMovimiento (item) {
      if (!item.vehicle?.dealer) return false

      const vehicleDealerId = item.vehicle.dealer.id
      const myDealerId = this.CURRENT_USER?.dealer?.id

      // Colaborador: solo los de su propio dealer
      if (this.$store.getters.HAS_ROLE(['colaborator'])) {
        if (!myDealerId) return false
        return vehicleDealerId === myDealerId
      }

      // Si el vehículo es de un dealer partner, SOLO verificar permiso cruzado (no verificar permiso local)
      if (vehicleDealerId !== myDealerId) {
        return this.$store.getters.HAS_PARTNER_PERMISSION({
          dealerId: vehicleDealerId,
          permission: 'eliminar_movimiento'
        })
      }

      // Si el vehículo es del dealer propio, verificar permiso local
      return this.HAS_PERMISSION('eliminar_movimiento')
    },

    async acceptMovimiento (item) {
      try {
        this.$confirm(`¿Está seguro que desea cambiar el estado del movimiento a "${this.getNextStatusName(item.status)}"?`).then(async result => {
          if (result) {
            this.loadingItems = true
            await MovimientoService.aceptarMovimiento(item.id).then((response) => {
              this.loadingItems = false
              if (response.error) {
                this.handleApiError(response, 'Ocurrió un error al cambiar el estado del movimiento')
                return
              }
              this.$toast.success('Estado del movimiento actualizado con éxito')
              this.loadData()
              this.loadStats()
            }).catch(error => {
              this.handleApiError(error, 'Ocurrió un error al cambiar el estado del movimiento')
              this.loadingItems = false
            })
          }
        })
      } catch (error) {
        this.handleApiError(error, 'Ocurrió un error al cambiar el estado del movimiento')
        this.loadingItems = false
      }
    },

    deleteMovimiento (item) {
      this.$confirm('¿Está seguro que desea eliminar este movimiento?').then(async (result) => {
        if (result) {
          try {
            await MovimientoService.delete(item.id)
            this.$toast.success('Movimiento eliminado con éxito')
            this.loadData()
            this.loadStats()
          } catch (error) {
            this.handleApiError(error, 'Ocurrió un error al eliminar el movimiento')
          }
        }
      })
    },

    getNextStatusName (currentStatus) {
      const statusOrder = ['unreported', 'reported', 'transport', 'processing', 'completed']
      const currentStatusIndex = statusOrder.indexOf(currentStatus)
      if (currentStatusIndex !== -1 && currentStatusIndex < statusOrder.length - 1) {
        return this.translateStatus(statusOrder[currentStatusIndex + 1])
      }
      return this.translateStatus(currentStatus)
    },

    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },

    getMovementDate (item) {
      return item.delivery_date || item.datetime
    },

    formatDateHours (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },

    translateStatus (status) {
      const statusMap = {
        'unreported': 'No reportado',
        'reported': 'Reportado',
        'transport': 'Recepcionado',
        'processing': 'En proceso',
        'completed': 'Completado'
      }
      return statusMap[status] || status
    },

    onPageChange (page) {
      this.pagination.page = page
      this.loadData()
    },

    onItemsPerPageChange (perPage) {
      this.pagination.itemsPerPage = perPage
      this.pagination.page = 1
      this.loadData()
    },

    getStatusColor (status) {
      const statusColorMap = {
        'unreported': 'red',
        'reported': 'orange',
        'transport': 'blue',
        'processing': 'purple',
        'completed': 'green'
      }
      return statusColorMap[status] || 'grey'
    },

    getStatusIcon (status) {
      const statusIconMap = {
        'unreported': 'mdi-alert-circle-outline',
        'reported': 'mdi-bell-ring-outline',
        'transport': 'mdi-truck-outline',
        'processing': 'mdi-progress-clock',
        'completed': 'mdi-check-circle-outline'
      }
      return statusIconMap[status] || 'mdi-help-circle-outline'
    },

    async toggleValidationCheckbox (item) {
      if (!this.HAS_PERMISSION('usage_chekbox_validacion')) return
      if (this.loadingCheckboxIds.includes(item.id)) return
      this.loadingCheckboxIds.push(item.id)
      try {
        const newValue = !item.validation_checkbox
        await MovimientoService.updateValidationCheckbox(item.id, newValue)
        item.validation_checkbox = newValue
      } catch (error) {
        this.handleApiError(error, 'Error al actualizar el checkbox de validación')
      } finally {
        this.loadingCheckboxIds = this.loadingCheckboxIds.filter(id => id !== item.id)
      }
    }
  },

  computed: {
    ...mapGetters([
      'MOVIMIENTOS',
      'CURRENT_USER_ROLES',
      'LOCATIONS',
      'CURRENT_USER',
      'HAS_PERMISSION',
      'DEALER_HAS_TYPE',
      // ← getters de partners (igual que Vehiculo.vue)
      'HAS_PARTNER_PERMISSION',
      'HAS_PERMISSION_ANYWHERE',
      'PARTNER_DEALER_IDS'
    ]),

    // ¿El dealer del usuario pertenece a algún grupo partner?
    hasPartnerDealers () {
      return this.$store.getters.IS_PARTNER || this.$store.getters.IN_SWITCHED_SESSION
    },

    hasDealersAsociados () {
      const user = this.$store.getters.CURRENT_USER
      if (!user || !user.dealer) return false
      return (
        (Array.isArray(user.dealers_asociados) && user.dealers_asociados.length > 0) ||
        !!user.dealer.is_colaborador
      )
    },

    viewMode: {
      get () {
        if (this.manualViewMode) return this.manualViewMode
        return this.$vuetify?.breakpoint?.mdAndUp ? 'table' : 'cards'
      },
      set (value) { this.manualViewMode = value }
    },

    // Headers dinámicos — muestra columna Dealer para colaboradores Y para partners
    headers () {
      let headers = [
        { text: 'Vehículo', align: 'start', value: 'vehicle', sortable: false, width: '25%' },
        { text: 'Ubicaciones', value: 'locations', sortable: false, width: '20%' }
      ]

      // Mostrar columna dealer si es colaborador O si tiene dealers asociados O si tiene dealers partner
      if (this.$store.getters.HAS_ROLE(['colaborator']) || this.hasDealersAsociados || this.hasPartnerDealers) {
        headers.push({ text: 'Dealer', value: 'dealer', sortable: false, width: '15%' })
      }

      headers = headers.concat([
        { text: 'Fecha entrega', value: 'datetime', width: '12%' },
        { text: 'Creación', value: 'created_at', width: '12%' },
        { text: 'Actualización', value: 'updated_at', width: '12%' },
        { text: 'Responsable', value: 'responsible', sortable: false, width: '10%' }
      ])

      if (this.HAS_PERMISSION('view_chekbox_validacion')) {
        headers.push({ text: 'Validación', value: 'validation_checkbox', sortable: false, align: 'center', width: '80px' })
      }

      headers = headers.concat([
        { text: 'Estado', value: 'status', align: 'center', width: '10%' },
        { text: 'Acciones', value: 'action', sortable: false, align: 'center', width: '120px' }
      ])

      return headers
    },

    pagination: {
      get () { return this.$store.getters.MOVIMIENTOS_PAG },
      set (value) { this.$store.commit('SET_MOVIMIENTOS_PAG', value) }
    },

    paginationText () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.totalItems)
      return `${start}-${end} de ${this.totalItems}`
    },

    searchTerm () { return this.filter.search },

    formattedDateRange () {
      if (this.dateRange && this.dateRange.length === 2) {
        const sortedDates = [...this.dateRange].sort()
        const fmt = (d) => {
          const date = new Date(d)
          return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`
        }
        return `${fmt(sortedDates[0])} - ${fmt(sortedDates[1])}`
      }
      return ''
    },

    hasActiveFilters () {
      return Object.values(this.filter).some(value => {
        if (Array.isArray(value)) return value.length > 0
        return value !== null && value !== undefined && value !== ''
      })
    },

    hasVehicleConditionPermissions () {
      const types = ['vn', 'vo', 'taller']
      return types.reduce((acc, type) => {
        return acc + (this.DEALER_HAS_TYPE(type) && this.HAS_PERMISSION(type) ? 1 : 0)
      }, 0) >= 1
    }
  },

  watch: {
    pagination: {
      handler: _.debounce(function (newVal, oldVal) {
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        if (relevantPropsChanged && !this.loadingItems) { this.loadData() }
      }, 200),
      deep: true
    },
    searchTerm () { this.debouncedLoadData() },
    manualViewMode (newVal, oldVal) {
      if (newVal === 'table' && oldVal === 'cards') {
        this.$nextTick(() => { this.tableId++; setTimeout(() => {}, 100) })
      }
    }
  }
}
</script>

<style scoped>
.type-option-card {
  cursor: pointer;
  border-radius: 8px;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.type-option-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-3px);
  border-color: var(--v-primary-base) !important;
}

.movement-card {
  border-radius: 8px;
  overflow: hidden;
}

.stat-card {
  border-radius: 8px;
  transition: all 0.3s;
  /* Force square cards - compact size */
  width: 110px;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
}

.stat-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-3px);
}

.stat-card .v-card__text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 8px !important;
  box-sizing: border-box;
}

.stat-card .text-h6 {
  line-height: 1;
  font-size: 1.25rem !important;
  margin: 2px 0;
}

.stat-card .text-caption {
  line-height: 1.1;
  font-size: 0.85rem !important;
  text-align: center;
  word-break: break-word;
  max-width: 100%;
}

.movement-item-card {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  border-radius: 8px;
}

.movement-item-card:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.action-buttons button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-buttons button:hover {
  opacity: 1;
}

/* Animación para nuevos elementos */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.new-item {
  animation: highlight 2s ease-in-out;
}

.search-input-mobile {
  max-width: 200px;
}

/* Estilos para filtros rápidos de ubicación */
.location-filter-chip {
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 180px;
}

.location-filter-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.location-filter-chip span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
  display: inline-block;
  vertical-align: middle;
}

/* Estilos para lista de ubicaciones en el menú */
.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

/* Estilos para tabla con headers pegajosos */
.sticky-table {
  position: relative;
}

.sticky-table ::v-deep .v-data-table__wrapper {
  overflow-y: auto;
  max-height: calc(100vh - 350px);
}

.sticky-table ::v-deep thead tr th {
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
  background: white !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

.sticky-table ::v-deep .v-data-table-header {
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
  background: white !important;
}

/* Para tema oscuro */
.theme--dark .sticky-table ::v-deep thead tr th {
  background: #1e1e1e !important;
}

.theme--dark .sticky-table ::v-deep .v-data-table-header {
  background: #1e1e1e !important;
}

/* Header flotante */
.floating-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-bottom: 1px solid #e0e0e0;
  animation: slideDown 0.3s ease-out;
  padding: 0 24px;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.theme--dark .floating-header {
  background: #1e1e1e;
  border-bottom: 1px solid #333;
}

/* Alternativa: Hacer toda la tabla sticky */
.sticky-table-container {
  position: sticky;
  top: 0;
  z-index: 5;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.theme--dark .sticky-table-container {
  background: #1e1e1e;
}
</style>
